import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { useRules } from '../context/RuleContext';
import { ruleSetToXml } from '../utils/xmlParser';
import config from '../config';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

function StatCard({ title, value, subtitle, color, onClick }) {
  return (
    <Card sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { boxShadow: 4, transform: 'translateY(-2px)' } : {}, transition: 'all 0.2s' }} onClick={onClick}>
      <CardContent sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{title}</Typography>
        {subtitle && <Typography variant="caption" color="text.disabled">{subtitle}</Typography>}
        {onClick && <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>Click to view XML</Typography>}
      </CardContent>
    </Card>
  );
}

function RuleXmlDialog({ open, onClose, title, rules, locationGroups, carrierGroups }) {
  const xml = useMemo(() => {
    if (!rules || rules.length === 0) return '';
    return ruleSetToXml({ locationGroups: [], carrierGroups: [], feeRules: rules, testRuleSet: [] });
  }, [rules]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: '80vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#1e1e1e', color: 'white', py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{title}</Typography>
          <Chip label={`${rules?.length || 0} rules`} size="small" sx={{ color: '#4caf50', bgcolor: 'rgba(76,175,80,0.15)' }} />
        </Box>
        <Box>
          <IconButton size="small" sx={{ color: 'white', mr: 1 }} onClick={() => navigator.clipboard.writeText(xml)}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ color: 'white' }} onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: '#0d1117', overflow: 'auto' }}>
        <pre style={{ margin: 0, padding: 16, fontFamily: '"Consolas", monospace', fontSize: '0.78rem', lineHeight: 1.7, color: '#c9d1d9', whiteSpace: 'pre-wrap' }}>
          {xml || 'No rules found'}
        </pre>
      </DialogContent>
      <DialogActions sx={{ bgcolor: '#1e1e1e' }}>
        <Button onClick={onClose} sx={{ color: 'white' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Analytics({ autoRunAi, onAiDone }) {
  const { feeRules, locationGroups, carrierGroups } = useRules();
  const [xmlDialog, setXmlDialog] = useState({ open: false, title: '', rules: [] });
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [autoTriggered, setAutoTriggered] = useState(false);

  const showXml = (title, rules) => setXmlDialog({ open: true, title, rules });

  const runAiAnalysis = async () => {
    if (aiAnalysis) { setAiOpen(true); return; }
    setAiLoading(true);
    setAiOpen(true);
    try {
      // Build a compact summary of rules for AI
      const summary = feeRules.slice(0, 80).map(r =>
        `#${r.sequenceNum}: carrier=${r.carrier||'**'}${r.origin ? ' o='+r.origin : ''}${r.destination ? ' d='+r.destination : ''}${r.feePercent != null ? ' %='+r.feePercent : ''}${r.feeAmount ? ' amt='+r.feeAmount : ''}${r.suppress === 'true' ? ' SUPPRESS' : ''}${r.cabinClass ? ' cabin='+r.cabinClass : ''}${r.priceSource ? ' src='+r.priceSource : ''}${r.travelDateDiscontinue ? ' until='+r.travelDateDiscontinue : ''}${r.minPrice ? ' min='+r.minPrice : ''}${r.maxPrice ? ' max='+r.maxPrice : ''}`
      ).join('\n');

      const carrierGroupSummary = carrierGroups.map(g => `Group U:${g.tableNum}: [${g.carriers.join(',')}]`).join('\n');

      const prompt = `You are a travel market analyst for Travomint OTA on Google Flights. Analyze the current rule file AND suggest market expansion opportunities.

CURRENT RULES (${feeRules.length} total):
${summary}

CARRIER GROUPS:
${carrierGroupSummary}

GOOGLE SUPPORTED AIRLINES (GDS - only these can have PriceSource:GDS rules):
2M,3F,3K,3M,4B,4N,4Y,4Z,5F,5O,5T,5U,5Z,6H,7C,9K,9N,9X,A3,AC,AH,AI,AM,AN,AR,AS,AT,AU,AV,AW,AY,AZ,B0,B4,BA,BB,BF,BG,BI,BP,BR,BT,BW,CG,CI,CM,CX,CY,CZ,E9,EK,EN,ET,EW,EY,F9,FC,FI,FJ,FZ,G3,GA,GF,GK,GL,GP,GQ,GR,GS,GX,H9,HA,HB,HC,HF,IB,IE,IZ,J2,JA,JJ,JL,JV,JX,JY,KC,KE,KF,KG,KM,KP,KQ,KR,KX,L6,LA,LF,LG,LH,LJ,LM,LO,LX,LY,M5,ME,MF,MH,MK,MO,MR,MS,MU,NF,NH,NP,NT,NZ,OA,OB,OK,OM,OS,OU,OZ,P0,P4,PD,PG,PK,PR,PU,PX,PY,QF,QR,QS,RA,RJ,RO,S4,SA,SB,SC,SK,SM,SN,SP,SQ,SS,SV,SY,T3,T7,TF,TG,TJ,TL,TM,TN,TS,TU,TX,U8,UB,UG,UK,UL,UP,UQ,UR,UU,UX,VA,VC,VN,VS,VT,WB,WJ,WM,WS,WY,X4,XK,XL,YN,YU,ZH,ZL,ZP

GOOGLE SUPPORTED AIRLINES (WEBSITE/Carrier - use PriceSource:WEBSITE):
2I,2L,2T,3K,3L,3O,4A,4Y,5E,5J,5W,6I,6J,7C,7G,8B,8P,9C,9M,9P,A3,A9,AD,AE,AK,AS,AY,B7,BC,BJ,BQ,BX,BZ,D7,D8,DD,DE,DG,DI,DU,DY,E5,ER,EW,F3,F8,FA,FD,FN,FO,FT,FW,FY,FZ,G4,G9,GD,GE,GK,GM,H2,H8,HD,HV,I2,I5,IF,IJ,IT,IW,IX,J9,JC,JL,JT,KC,KK,KW,LH,LJ,LK,LS,LX,LZ,MM,MX,N0,NH,NK,NM,NO,NQ,NU,NZ,OC,OD,OG,OI,OR,OS,P5,P6,PA,PB,PC,Q3,QG,QH,QN,QP,QQ,QV,QZ,S7,S8,SG,SI,SL,SN,SQ,TB,TO,TR,TW,UI,UO,V7,VB,VE,VF,VJ,VR,VU,VY,VZ,W4,W6,W9,WG,WK,WV,X3,XC,XJ,XP,XQ,XR,XY,XZ,YB,YP,YT,Z0,Z2,ZG

Provide analysis in this EXACT JSON format:
{
  "summary": "Executive summary of current state and market opportunity",
  "score": 75,
  "coverage": "Current coverage assessment",
  "competitivePosition": "How this compares to other OTAs on Google Flights",
  "revenueOpportunity": "Estimated revenue improvement areas",
  "countryData": [{"country": "US", "rules": 12, "avgDiscount": 4.5, "airlines": 8, "status": "covered"}, {"country": "IN", "rules": 5, "avgDiscount": 3, "airlines": 4, "status": "partial"}],
  "missingMarkets": [{"country": "DE", "priority": "high", "reason": "Large European market with no coverage", "suggestedAirlines": ["LH","EW"], "suggestedDiscount": 3}, {"country": "JP", "priority": "medium", "reason": "Growing Asia-Pacific traffic", "suggestedAirlines": ["NH","JL"], "suggestedDiscount": 4}],
  "suggestedAdditions": [{"airline": "TG", "country": "TH", "discount": 4, "priceSource": "GDS", "reason": "Thai Airways has high traffic Thailand routes, no rule exists"}, {"airline": "SQ", "country": "SG", "discount": 3, "priceSource": "GDS", "reason": "Singapore Airlines premium market"}],
  "airlineDiscounts": [{"airline": "AI", "discount": 5, "markets": 3}, {"airline": "EK", "discount": 3, "markets": 2}],
  "marketInsights": [{"market": "US-India", "status": "strong", "detail": "Well covered with 5% across multiple airlines"}, {"market": "UK-UAE", "status": "weak", "detail": "Only generic rules, no airline-specific"}],
  "insights": ["insight about strategy", "insight about pricing", "insight about coverage", "insight about competition"],
  "loopholes": ["specific gap 1", "specific gap 2", "specific gap 3"],
  "recommendations": ["action 1", "action 2", "action 3"],
  "riskAreas": ["risk 1", "risk 2"],
  "cabinAnalysis": {"economy": 60, "business": 10, "first": 0, "all": 30},
  "sourceAnalysis": {"gds": 70, "website": 20, "both": 10}
}

IMPORTANT: 
- Only suggest airlines from the SUPPORTED lists above
- Consider high-traffic routes: US-India, US-Europe, UK-India, UK-US, Australia-Asia, Canada-India, Middle East hubs
- Suggest realistic discounts (2-6% typical, max 8% for economy bulk)
- Identify which countries/routes have ZERO coverage currently
- Consider CURRENT DATE is July 2026. Think about upcoming seasonal events and peak periods:
  * Summer holidays (Jun-Aug): Europe, US domestic
  * Diwali/Festive (Oct-Nov): India traffic surge
  * Christmas/New Year (Dec-Jan): Worldwide peak
  * Chinese New Year (Jan-Feb): Asia-Pacific
  * Hajj/Umrah: Middle East seasonal
  * School holidays: Australia (Dec-Jan), UK (Jul-Aug), US (Jun-Aug)
  * Business travel peaks: Sep-Nov, Jan-Mar
- For each suggestion, mention if there's an upcoming event/season that makes it timely
- Suggest specific routes where adding a discount NOW would capture maximum bookings
- Include "eventOpportunities" showing upcoming events and which airlines/routes to target

Add this to the JSON:
  "eventOpportunities": [{"event": "Diwali Season Oct-Nov 2026", "routes": ["US-IN", "UK-IN", "CA-IN"], "airlines": ["AI", "EK", "QR"], "suggestedDiscount": 5, "impact": "High - 40% increase in India-bound traffic", "action": "Add 5% discount for AI,EK,QR on US/UK/CA to IN routes for Oct-Nov 2026"}],
  "seasonalStrategy": [{"period": "Jul-Aug 2026", "focus": "Summer Europe traffic", "suggestion": "Increase LH,AF discounts to 4% for US-Europe routes"}, {"period": "Oct-Nov 2026", "focus": "Diwali/Festive India", "suggestion": "Add AI,EK with 5% discount for all origins to IN"}],
  "topBookingRoutes": [{"route": "US-IN", "airline": "AI", "potential": "very high", "currentDiscount": 5, "suggested": "Keep"}, {"route": "UK-TH", "airline": "TG", "potential": "high", "currentDiscount": 0, "suggested": "Add 4%"}]

- Return ONLY valid JSON, no extra text`;

      const res = await fetch(`${config.API_BASE}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: [{ role: 'user', content: prompt }], existingRulesContext: '' }),
      });
      const data = await res.json();
      if (data.success && data.reply) {
        const jsonMatch = data.reply.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try { setAiAnalysis(JSON.parse(jsonMatch[0])); } catch { setAiAnalysis({ summary: data.reply, insights: [], loopholes: [], recommendations: [], riskAreas: [], coverage: '', score: 0, countryData: [], marketInsights: [], airlineDiscounts: [] }); }
        } else {
          setAiAnalysis({ summary: data.reply, insights: [], loopholes: [], recommendations: [], riskAreas: [], coverage: '', score: 0, countryData: [], marketInsights: [], airlineDiscounts: [] });
        }
      }
    } catch { setAiAnalysis({ summary: 'Failed to get AI analysis.', insights: [], loopholes: [], recommendations: [], riskAreas: [], coverage: '', score: 0, countryData: [], marketInsights: [], airlineDiscounts: [] }); }
    finally { setAiLoading(false); }
  };

  // Auto-run AI analysis when redirected from chatbot
  React.useEffect(() => {
    if (autoRunAi && !autoTriggered && feeRules.length > 0) {
      setAutoTriggered(true);
      if (onAiDone) onAiDone();
      runAiAnalysis();
    }
  }, [autoRunAi, feeRules.length]);

  const analytics = useMemo(() => {
    const now = new Date();
    const res = {
      total: feeRules.length, suppressed: 0, discounts: 0, markups: 0, flat: 0, percent: 0,
      economyRules: 0, businessRules: 0, firstRules: 0,
      gdsRules: 0, websiteRules: 0,
      expiredCount: 0, expiringSoon: 0, activeWithDates: 0,
      oneWayRules: 0, roundTripRules: 0,
      carrierBreakdown: {}, originBreakdown: {}, destinationBreakdown: {},
      avgDiscountPct: 0, avgMarkupPct: 0,
      maxDiscount: { value: 0, rule: null }, maxMarkup: { value: 0, rule: null },
      interlineBlocked: 0, priceRangeRules: 0, carrierGroupRules: 0, wildcardRules: 0,
      airlineDetails: {},
    };
    let discountPctSum = 0, discountPctCount = 0;

    feeRules.forEach(r => {
      if (r.suppress === 'true') { res.suppressed++; }
      else {
        const pct = r.feePercent != null ? parseFloat(r.feePercent) : null;
        const hasFlat = !!r.feeAmount;
        const isDiscount = (pct != null && pct < 0) || (r.feeAmount && r.feeAmount.includes('-'));
        if (isDiscount) { res.discounts++; } else { res.markups++; }
        if (pct != null) res.percent++;
        if (hasFlat && pct == null) res.flat++;
        if (pct != null && pct < 0) { discountPctSum += Math.abs(pct); discountPctCount++; if (Math.abs(pct) > res.maxDiscount.value) res.maxDiscount = { value: Math.abs(pct), rule: r }; }
        if (pct != null && pct > 0 && pct > res.maxMarkup.value) res.maxMarkup = { value: pct, rule: r };
      }
      if (r.cabinClass === 'ECONOMY') res.economyRules++;
      if (r.cabinClass === 'BUSINESS') res.businessRules++;
      if (r.cabinClass === 'FIRST') res.firstRules++;
      if (r.priceSource === 'GDS') res.gdsRules++;
      if (r.priceSource === 'WEBSITE') res.websiteRules++;
      if (r.tripType === 'ONE_WAY') res.oneWayRules++;
      if (r.tripType === 'ROUND_TRIP') res.roundTripRules++;
      if (r.travelDateDiscontinue) {
        const d = new Date(r.travelDateDiscontinue);
        if (d < now) res.expiredCount++;
        else if (d < new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)) res.expiringSoon++;
        else res.activeWithDates++;
      }
      const c = r.carrier || '**';
      res.carrierBreakdown[c] = (res.carrierBreakdown[c] || 0) + 1;
      if (c === '**') res.wildcardRules++;
      if (c.startsWith('U:')) res.carrierGroupRules++;
      if (r.origin) res.originBreakdown[r.origin] = (res.originBreakdown[r.origin] || 0) + 1;
      if (r.destination) res.destinationBreakdown[r.destination] = (res.destinationBreakdown[r.destination] || 0) + 1;
      if (r.interline === 'false' || r.sliceInterline === 'false') res.interlineBlocked++;
      if (r.minPrice || r.maxPrice) res.priceRangeRules++;

      // Airline details
      if (c !== '**' && !c.startsWith('U:')) {
        if (!res.airlineDetails[c]) res.airlineDetails[c] = { rules: [], discounts: 0, markups: 0, suppress: 0, flat: 0, percent: 0, origins: new Set(), destinations: new Set() };
        res.airlineDetails[c].rules.push(r);
        if (r.suppress === 'true') res.airlineDetails[c].suppress++;
        else {
          const pctV = r.feePercent != null ? parseFloat(r.feePercent) : null;
          if ((pctV != null && pctV < 0) || (r.feeAmount && r.feeAmount.includes('-'))) res.airlineDetails[c].discounts++;
          else res.airlineDetails[c].markups++;
          if (pctV != null) res.airlineDetails[c].percent++;
          if (r.feeAmount && pctV == null) res.airlineDetails[c].flat++;
        }
        if (r.origin) res.airlineDetails[c].origins.add(r.origin);
        if (r.destination) res.airlineDetails[c].destinations.add(r.destination);
      }
    });
    res.avgDiscountPct = discountPctCount > 0 ? (discountPctSum / discountPctCount).toFixed(1) : '0';
    return res;
  }, [feeRules]);

  // Charts
  const typeChart = { labels: ['Markups', 'Discounts', 'Suppression'], datasets: [{ data: [analytics.markups, analytics.discounts, analytics.suppressed], backgroundColor: ['#4caf50', '#ff9800', '#f44336'] }] };
  const feeTypeChart = { labels: ['Percentage', 'Flat Amount', 'Suppress'], datasets: [{ data: [analytics.percent, analytics.flat, analytics.suppressed], backgroundColor: ['#2196f3', '#9c27b0', '#f44336'] }] };

  const topCarriers = Object.entries(analytics.carrierBreakdown).filter(([k]) => k !== '**' && !k.startsWith('U:')).sort((a, b) => b[1] - a[1]).slice(0, 12);
  const carrierChart = { labels: topCarriers.map(([k]) => k), datasets: [{ label: 'Rules', data: topCarriers.map(([, v]) => v), backgroundColor: '#1976d2' }] };

  const topOrigins = Object.entries(analytics.originBreakdown).sort((a, b) => b[1] - a[1]).slice(0, 10);
  const originChart = { labels: topOrigins.map(([k]) => k.replace('N:', '')), datasets: [{ label: 'Rules', data: topOrigins.map(([, v]) => v), backgroundColor: '#ff9800' }] };

  if (feeRules.length === 0) {
    return (<Box><Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>Analytics</Typography>
      <Card><CardContent sx={{ textAlign: 'center', py: 6 }}><Typography color="text.secondary">Upload or sync a rule file to see analytics.</Typography></CardContent></Card></Box>);
  }

  return (
    <Box>
      {/* Full-page AI Analysis Loader */}
      {aiLoading && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1300,
          bgcolor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
        }}>
          <style>{`@keyframes aiPulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.2); opacity: 0.7; } }`}</style>
          <IconButton onClick={() => setAiLoading(false)} sx={{ position: 'absolute', top: 24, right: 24, bgcolor: 'rgba(0,0,0,0.05)', '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' } }}>
            <CloseIcon />
          </IconButton>
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={80} thickness={2} sx={{ color: '#4caf50' }} />
            <AutoAwesomeIcon sx={{ position: 'absolute', fontSize: 32, color: '#4caf50', animation: 'aiPulse 1.5s infinite' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#4caf50' }}>AI Analyzing Your Rules</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
            Scanning {feeRules.length} rules across {carrierGroups.length} carrier groups for insights, loopholes, and market opportunities...
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Chip label="📊 Market Coverage" size="small" variant="outlined" color="success" />
            <Chip label="✈️ Airline Analysis" size="small" variant="outlined" color="success" />
            <Chip label="🌍 Route Gaps" size="small" variant="outlined" color="success" />
            <Chip label="📅 Seasonal Strategy" size="small" variant="outlined" color="success" />
            <Chip label="💰 Revenue Opportunities" size="small" variant="outlined" color="success" />
          </Box>
          <LinearProgress color="success" sx={{ width: 300, mt: 2, borderRadius: 2, height: 6 }} />
        </Box>
      )}

      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>Analytics Dashboard</Typography>

      {/* AI Analysis Button */}
      <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main', borderRadius: 2, boxShadow: '0 0 10px rgba(25,118,210,0.1)', background: 'linear-gradient(135deg, rgba(25,118,210,0.03) 0%, rgba(156,39,176,0.03) 100%)' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AutoAwesomeIcon color="primary" />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>AI-Powered Analysis</Typography>
              <Typography variant="caption" color="text.secondary">Get insights, find loopholes, and optimization recommendations</Typography>
            </Box>
          </Box>
          <Button variant="contained" startIcon={aiLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={runAiAnalysis} disabled={aiLoading} sx={{ borderRadius: 2, px: 3 }}>
            {aiLoading ? 'Analyzing...' : aiAnalysis ? 'View Analysis' : 'Run AI Analysis'}
          </Button>
        </CardContent>
      </Card>

      {/* AI Analysis Results */}
      <Collapse in={aiOpen && !!aiAnalysis}>
        {aiAnalysis && (
          <Card sx={{ mb: 3, bgcolor: '#fafbff', border: '1px solid #e3e8f0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AutoAwesomeIcon color="primary" fontSize="small" /> AI Analysis Report
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">Health Score:</Typography>
                  <Chip label={`${aiAnalysis.score}/100`} size="small"
                    color={aiAnalysis.score >= 80 ? 'success' : aiAnalysis.score >= 50 ? 'warning' : 'error'} />
                  <Button size="small" onClick={() => setAiOpen(false)}>Hide</Button>
                </Box>
              </Box>

              {/* Summary */}
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                <Typography variant="body2">{aiAnalysis.summary}</Typography>
              </Alert>

              {/* Charts Row - Country Coverage & Airline Discounts */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {aiAnalysis.countryData?.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>📊 Country Rule Coverage</Typography>
                        <Bar data={{
                          labels: aiAnalysis.countryData.map(d => d.country),
                          datasets: [
                            { label: 'Rules', data: aiAnalysis.countryData.map(d => d.rules), backgroundColor: '#1976d2' },
                            { label: 'Airlines', data: aiAnalysis.countryData.map(d => d.airlines), backgroundColor: '#4caf50' },
                          ]
                        }} options={{ responsive: true, plugins: { legend: { position: 'bottom' } }, scales: { y: { beginAtZero: true } } }} />
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {aiAnalysis.airlineDiscounts?.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>✈️ Airline Discount Levels (%)</Typography>
                        <Bar data={{
                          labels: aiAnalysis.airlineDiscounts.map(d => d.airline),
                          datasets: [{ label: 'Discount %', data: aiAnalysis.airlineDiscounts.map(d => d.discount), backgroundColor: '#ff9800' }]
                        }} options={{ responsive: true, indexAxis: 'y', plugins: { legend: { display: false } }, scales: { x: { beginAtZero: true } } }} />
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>

              {/* Market Insights Table */}
              {aiAnalysis.marketInsights?.length > 0 && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>🌍 Market Coverage Analysis</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem' } }}>
                            <TableCell>Market/Route</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Detail</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {aiAnalysis.marketInsights.map((m, i) => (
                            <TableRow key={i}>
                              <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{m.market}</Typography></TableCell>
                              <TableCell>
                                <Chip label={m.status} size="small"
                                  color={m.status === 'strong' ? 'success' : m.status === 'moderate' ? 'warning' : 'error'}
                                  variant="outlined" sx={{ textTransform: 'capitalize' }} />
                              </TableCell>
                              <TableCell><Typography variant="caption">{m.detail}</Typography></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {/* Missing Markets - Where we are lacking */}
              {aiAnalysis.missingMarkets?.length > 0 && (
                <Card variant="outlined" sx={{ mb: 2, borderColor: '#e91e63' }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#e91e63' }}>🚨 Missing Markets — Where You're Lacking</Typography>
                    <Grid container spacing={1.5}>
                      {aiAnalysis.missingMarkets.map((m, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                          <Card variant="outlined" sx={{ bgcolor: m.priority === 'high' ? '#fff3e0' : m.priority === 'medium' ? '#f3f8ff' : '#f9f9f9' }}>
                            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{m.country}</Typography>
                                <Chip label={m.priority} size="small" color={m.priority === 'high' ? 'error' : m.priority === 'medium' ? 'warning' : 'default'} sx={{ height: 20, fontSize: '0.6rem' }} />
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{m.reason}</Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {m.suggestedAirlines?.map(a => <Chip key={a} label={a} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#e3f2fd' }} />)}
                                <Chip label={`~${m.suggestedDiscount}%`} size="small" color="warning" sx={{ height: 20, fontSize: '0.65rem' }} />
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Suggested Airline Additions */}
              {aiAnalysis.suggestedAdditions?.length > 0 && (
                <Card variant="outlined" sx={{ mb: 2, borderColor: '#4caf50' }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#4caf50' }}>✈️ Suggested Airlines to Add (Google Supported)</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem', bgcolor: '#f1f8e9' } }}>
                            <TableCell>Airline</TableCell>
                            <TableCell>Market</TableCell>
                            <TableCell>Suggested Discount</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {aiAnalysis.suggestedAdditions.map((s, i) => (
                            <TableRow key={i} hover>
                              <TableCell><Chip label={s.airline} size="small" color="primary" /></TableCell>
                              <TableCell><Chip label={s.country} size="small" variant="outlined" /></TableCell>
                              <TableCell><Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800' }}>{s.discount}%</Typography></TableCell>
                              <TableCell><Chip label={s.priceSource} size="small" sx={{ height: 20, fontSize: '0.6rem' }} /></TableCell>
                              <TableCell><Typography variant="caption">{s.reason}</Typography></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {/* Cabin & Source Analysis Charts */}
              {(aiAnalysis.cabinAnalysis || aiAnalysis.sourceAnalysis) && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {aiAnalysis.cabinAnalysis && (
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>💺 Cabin Class Distribution (%)</Typography>
                          <Doughnut data={{
                            labels: ['Economy', 'Business', 'First', 'All Cabins'],
                            datasets: [{ data: [aiAnalysis.cabinAnalysis.economy, aiAnalysis.cabinAnalysis.business, aiAnalysis.cabinAnalysis.first, aiAnalysis.cabinAnalysis.all], backgroundColor: ['#2196f3', '#9c27b0', '#f44336', '#4caf50'] }]
                          }} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                  {aiAnalysis.sourceAnalysis && (
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>📡 Price Source Distribution (%)</Typography>
                          <Doughnut data={{
                            labels: ['GDS', 'Website', 'Both/Any'],
                            datasets: [{ data: [aiAnalysis.sourceAnalysis.gds, aiAnalysis.sourceAnalysis.website, aiAnalysis.sourceAnalysis.both], backgroundColor: ['#00796b', '#ff5722', '#607d8b'] }]
                          }} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              )}

              {/* Event Opportunities */}
              {aiAnalysis.eventOpportunities?.length > 0 && (
                <Card variant="outlined" sx={{ mb: 2, borderColor: '#7c4dff', background: 'linear-gradient(135deg, rgba(124,77,255,0.03) 0%, rgba(255,64,129,0.03) 100%)' }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#7c4dff' }}>📅 Event-Based Booking Opportunities</Typography>
                    <Grid container spacing={1.5}>
                      {aiAnalysis.eventOpportunities.map((ev, i) => (
                        <Grid item xs={12} md={6} key={i}>
                          <Card variant="outlined" sx={{ bgcolor: '#fafafa' }}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>🎯 {ev.event}</Typography>
                                <Chip label={ev.impact?.split(' - ')[0] || 'High'} size="small"
                                  color={ev.impact?.includes('High') ? 'error' : 'warning'} sx={{ height: 20, fontSize: '0.6rem' }} />
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                                {ev.routes?.map(r => <Chip key={r} label={r} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.6rem' }} />)}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                                {ev.airlines?.map(a => <Chip key={a} label={a} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#e3f2fd' }} />)}
                                <Chip label={`${ev.suggestedDiscount}% discount`} size="small" color="warning" sx={{ height: 20, fontSize: '0.6rem' }} />
                              </Box>
                              <Typography variant="caption" color="text.secondary">{ev.action}</Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Seasonal Strategy */}
              {aiAnalysis.seasonalStrategy?.length > 0 && (
                <Card variant="outlined" sx={{ mb: 2, borderColor: '#00bcd4' }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#00bcd4' }}>🗓️ Seasonal Strategy Roadmap</Typography>
                    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                      {aiAnalysis.seasonalStrategy.map((s, i) => (
                        <Card key={i} variant="outlined" sx={{ minWidth: 220, flex: '0 0 auto', bgcolor: '#f0fffe' }}>
                          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Chip label={s.period} size="small" color="info" sx={{ mb: 0.5, fontSize: '0.65rem' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.3 }}>{s.focus}</Typography>
                            <Typography variant="caption" color="text.secondary">{s.suggestion}</Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Top Booking Routes */}
              {aiAnalysis.topBookingRoutes?.length > 0 && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>🔥 Top Booking Potential Routes</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem', bgcolor: '#fff8e1' } }}>
                            <TableCell>Route</TableCell>
                            <TableCell>Airline</TableCell>
                            <TableCell>Potential</TableCell>
                            <TableCell>Current Discount</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {aiAnalysis.topBookingRoutes.map((r, i) => (
                            <TableRow key={i} hover>
                              <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{r.route}</Typography></TableCell>
                              <TableCell><Chip label={r.airline} size="small" color="primary" /></TableCell>
                              <TableCell>
                                <Chip label={r.potential} size="small"
                                  color={r.potential === 'very high' ? 'error' : r.potential === 'high' ? 'warning' : 'default'}
                                  sx={{ textTransform: 'capitalize' }} />
                              </TableCell>
                              <TableCell>{r.currentDiscount > 0 ? `${r.currentDiscount}%` : <Chip label="None" size="small" color="error" variant="outlined" sx={{ height: 20 }} />}</TableCell>
                              <TableCell><Typography variant="caption" sx={{ fontWeight: 500, color: r.suggested === 'Keep' ? '#4caf50' : '#ff9800' }}>{r.suggested}</Typography></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {/* Coverage & Competitive Position */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {aiAnalysis.coverage && (
                  <Grid item xs={12} md={6}>
                    <Alert severity="success" icon={<CheckCircleIcon />} sx={{ borderRadius: 2, height: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Coverage Assessment</Typography>
                      <Typography variant="body2">{aiAnalysis.coverage}</Typography>
                    </Alert>
                  </Grid>
                )}
                {aiAnalysis.competitivePosition && (
                  <Grid item xs={12} md={6}>
                    <Alert severity="info" icon={<LightbulbIcon />} sx={{ borderRadius: 2, height: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Competitive Position</Typography>
                      <Typography variant="body2">{aiAnalysis.competitivePosition}</Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>

              {aiAnalysis.revenueOpportunity && (
                <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>💰 Revenue Opportunity</Typography>
                  <Typography variant="body2">{aiAnalysis.revenueOpportunity}</Typography>
                </Alert>
              )}

              <Grid container spacing={2}>
                {/* Insights */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LightbulbIcon fontSize="small" color="info" /> Key Insights
                      </Typography>
                      {aiAnalysis.insights?.map((insight, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                          <Typography variant="body2" color="info.main" sx={{ fontWeight: 700, minWidth: 18 }}>{i+1}.</Typography>
                          <Typography variant="body2">{insight}</Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Loopholes */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%', borderColor: '#ff9800' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, color: 'warning.main' }}>
                        <WarningIcon fontSize="small" /> Loopholes & Gaps
                      </Typography>
                      {aiAnalysis.loopholes?.map((loophole, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                          <Typography variant="body2" color="warning.main" sx={{ fontWeight: 700, minWidth: 18 }}>⚠️</Typography>
                          <Typography variant="body2">{loophole}</Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Recommendations */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%', borderColor: '#4caf50' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                        <TipsAndUpdatesIcon fontSize="small" /> Recommendations
                      </Typography>
                      {aiAnalysis.recommendations?.map((rec, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                          <Typography variant="body2" color="success.main" sx={{ fontWeight: 700, minWidth: 18 }}>✓</Typography>
                          <Typography variant="body2">{rec}</Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Risk Areas */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%', borderColor: '#f44336' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main' }}>
                        <WarningIcon fontSize="small" color="error" /> Risk Areas
                      </Typography>
                      {aiAnalysis.riskAreas?.map((risk, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                          <Typography variant="body2" color="error.main" sx={{ fontWeight: 700, minWidth: 18 }}>🔴</Typography>
                          <Typography variant="body2">{risk}</Typography>
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Button size="small" variant="outlined" onClick={() => { setAiAnalysis(null); setAiOpen(false); }}>
                  Re-analyze
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Collapse>

      {/* KPI Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}><StatCard title="Total Rules" value={analytics.total} color="#1976d2" onClick={() => showXml('All Rules', feeRules)} /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatCard title="Discounts" value={analytics.discounts} color="#ff9800" subtitle={`Avg: ${analytics.avgDiscountPct}%`} onClick={() => showXml('Discount Rules', feeRules.filter(r => (r.feePercent != null && parseFloat(r.feePercent) < 0) || (r.feeAmount && r.feeAmount.includes('-'))))} /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatCard title="Markups" value={analytics.markups} color="#4caf50" onClick={() => showXml('Markup Rules', feeRules.filter(r => r.suppress !== 'true' && !((r.feePercent != null && parseFloat(r.feePercent) < 0) || (r.feeAmount && r.feeAmount.includes('-')))))} /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatCard title="Suppressed" value={analytics.suppressed} color="#f44336" onClick={() => showXml('Suppress Rules', feeRules.filter(r => r.suppress === 'true'))} /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatCard title="Expired" value={analytics.expiredCount} color="#9e9e9e" onClick={() => showXml('Expired Rules', feeRules.filter(r => r.travelDateDiscontinue && new Date(r.travelDateDiscontinue) < new Date()))} /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatCard title="Expiring 30d" value={analytics.expiringSoon} color="#e91e63" onClick={() => showXml('Expiring Soon', feeRules.filter(r => { const d = r.travelDateDiscontinue ? new Date(r.travelDateDiscontinue) : null; return d && d >= new Date() && d < new Date(Date.now() + 30*86400000); }))} /></Grid>
      </Grid>

      {/* Row 2 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}><StatCard title="Percentage Rules" value={analytics.percent} color="#2196f3" onClick={() => showXml('Percentage Rules', feeRules.filter(r => r.feePercent != null))} /></Grid>
        <Grid item xs={6} sm={3}><StatCard title="Flat Amount" value={analytics.flat} color="#9c27b0" onClick={() => showXml('Flat Amount Rules', feeRules.filter(r => r.feeAmount && r.feePercent == null))} /></Grid>
        <Grid item xs={6} sm={3}><StatCard title="GDS Source" value={analytics.gdsRules} color="#00796b" onClick={() => showXml('GDS Rules', feeRules.filter(r => r.priceSource === 'GDS'))} /></Grid>
        <Grid item xs={6} sm={3}><StatCard title="Website Source" value={analytics.websiteRules} color="#5d4037" onClick={() => showXml('Website Rules', feeRules.filter(r => r.priceSource === 'WEBSITE'))} /></Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}><CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Rule Types</Typography>
            <Doughnut data={typeChart} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}><CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Fee Format</Typography>
            <Doughnut data={feeTypeChart} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}><CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Country Coverage (Origins)</Typography>
            {topOrigins.length > 0 ? <Bar data={originChart} options={{ responsive: true, indexAxis: 'y', plugins: { legend: { display: false } } }} /> : <Typography color="text.secondary">No origin-specific rules</Typography>}
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* Airline Bar Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Top Airlines by Rule Count</Typography>
          {topCarriers.length > 0 ? <Bar data={carrierChart} options={{ responsive: true, plugins: { legend: { display: false } }, onClick: (e, elements) => { if (elements.length > 0) { const idx = elements[0].index; const carrier = topCarriers[idx][0]; showXml(`Rules for ${carrier}`, feeRules.filter(r => r.carrier === carrier)); } } }} /> : <Typography color="text.secondary">No specific airline rules</Typography>}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Click a bar to view XML for that airline</Typography>
        </CardContent>
      </Card>

      {/* Airline Detail Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Airline-Specific Rule Breakdown</Typography>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f7fa' } }}>
                  <TableCell>Airline</TableCell>
                  <TableCell align="center">Rules</TableCell>
                  <TableCell align="center">Discounts</TableCell>
                  <TableCell align="center">Markups</TableCell>
                  <TableCell align="center">Suppress</TableCell>
                  <TableCell align="center">% Rules</TableCell>
                  <TableCell align="center">Flat Rules</TableCell>
                  <TableCell>Origins</TableCell>
                  <TableCell>Destinations</TableCell>
                  <TableCell align="center">Coverage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(analytics.airlineDetails).sort((a, b) => b[1].rules.length - a[1].rules.length).map(([code, data]) => (
                  <TableRow key={code} hover sx={{ cursor: 'pointer' }} onClick={() => showXml(`Rules for ${code}`, data.rules)}>
                    <TableCell><Chip label={code} size="small" color="primary" variant="outlined" /></TableCell>
                    <TableCell align="center"><strong>{data.rules.length}</strong></TableCell>
                    <TableCell align="center">{data.discounts > 0 ? <Chip label={data.discounts} size="small" color="warning" /> : '—'}</TableCell>
                    <TableCell align="center">{data.markups > 0 ? <Chip label={data.markups} size="small" color="success" /> : '—'}</TableCell>
                    <TableCell align="center">{data.suppress > 0 ? <Chip label={data.suppress} size="small" color="error" /> : '—'}</TableCell>
                    <TableCell align="center">{data.percent}</TableCell>
                    <TableCell align="center">{data.flat}</TableCell>
                    <TableCell><Typography variant="caption">{[...data.origins].map(o => o.replace('N:', '')).join(', ') || 'ALL'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{[...data.destinations].map(d => d.replace('N:', '')).join(', ') || 'ALL'}</Typography></TableCell>
                    <TableCell align="center" sx={{ width: 100 }}>
                      <LinearProgress variant="determinate" value={(data.rules.length / analytics.total) * 100} sx={{ height: 6, borderRadius: 3 }} />
                      <Typography variant="caption">{((data.rules.length / analytics.total) * 100).toFixed(0)}%</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Click any row to view the raw XML for that airline's rules</Typography>
        </CardContent>
      </Card>

      {/* Country Coverage Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Country-wise Airline Coverage</Typography>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f7fa' } }}>
                  <TableCell>Origin Country</TableCell>
                  <TableCell align="center">Rule Count</TableCell>
                  <TableCell>Airlines</TableCell>
                  <TableCell align="center">Coverage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topOrigins.map(([origin, count]) => {
                  const originRules = feeRules.filter(r => r.origin === origin);
                  const airlines = [...new Set(originRules.map(r => r.carrier).filter(c => c && c !== '**' && !c.startsWith('U:')))];
                  return (
                    <TableRow key={origin} hover sx={{ cursor: 'pointer' }} onClick={() => showXml(`Rules for origin: ${origin}`, originRules)}>
                      <TableCell><Chip label={origin.replace('N:', '')} size="small" variant="outlined" /></TableCell>
                      <TableCell align="center"><strong>{count}</strong></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                          {airlines.slice(0, 8).map(a => <Chip key={a} label={a} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />)}
                          {airlines.length > 8 && <Chip label={`+${airlines.length - 8}`} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />}
                          {airlines.length === 0 && <Typography variant="caption" color="text.secondary">All carriers</Typography>}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ width: 100 }}>
                        <LinearProgress variant="determinate" value={(count / analytics.total) * 100} sx={{ height: 6, borderRadius: 3 }} color="warning" />
                        <Typography variant="caption">{((count / analytics.total) * 100).toFixed(0)}%</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Click any row to view the raw XML for that country's rules</Typography>
        </CardContent>
      </Card>

      {/* XML Dialog */}
      <RuleXmlDialog open={xmlDialog.open} onClose={() => setXmlDialog({ ...xmlDialog, open: false })} title={xmlDialog.title} rules={xmlDialog.rules} locationGroups={locationGroups} carrierGroups={carrierGroups} />
    </Box>
  );
}
